<template>
  <div class="chart-display">
    <!-- Loading State -->
    <div v-if="loading" class="cd-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Generating chart…</span>
      </div>
      <p class="mt-3 text-muted">Generating your chart…</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="cd-state cd-state-error">
      <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
      <h5 class="text-danger mb-1">Could not generate this chart</h5>
      <p class="text-muted mb-3">{{ error }}</p>
      <button @click="$emit('retry')" class="btn btn-outline-primary btn-sm">
        <i class="fas fa-redo"></i> Try again
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!chart" class="cd-state">
      <i class="fas fa-chart-line fa-3x text-muted mb-3"></i>
      <h5 class="text-muted">No figure yet</h5>
      <p class="text-muted mb-0">
        Pick a dataset, choose a figure type, configure its axes, then hit
        <strong>Generate chart</strong>.
      </p>
    </div>

    <!-- Chart.js missing -->
    <div v-else-if="!chartJsAvailable" class="cd-state cd-state-error">
      <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
      <h5 class="text-warning">Chart.js not loaded</h5>
      <p class="text-muted">
        The Figures plugin needs Chart.js available at <code>window.Chart</code>
        or injected as <code>this.$Chart</code>. Check your app's Chart.js setup.
      </p>
    </div>

    <!-- Chart -->
    <div v-else class="cd-chart-wrap">
      <div class="cd-canvas-wrap">
        <canvas ref="canvas" />
      </div>

      <!-- Stats panel -->
      <div v-if="hasStats" class="cd-stats">
        <!-- Summary table -->
        <div v-if="chart.stats && chart.stats.summary" class="cd-stat-block">
          <h6><i class="fas fa-calculator"></i> Summary statistics</h6>
          <div class="cd-stat-grid">
            <div v-for="s in summaryRows(chart.stats.summary)" :key="s.label" class="cd-stat-cell">
              <span class="cd-stat-label">{{ s.label }}</span>
              <span class="cd-stat-value">{{ s.value }}</span>
            </div>
          </div>
        </div>

        <!-- Regression -->
        <div v-if="chart.stats && chart.stats.regression" class="cd-stat-block">
          <h6><i class="fas fa-draw-polygon"></i> Linear regression</h6>
          <div class="cd-stat-grid">
            <div class="cd-stat-cell">
              <span class="cd-stat-label">y = mx + b</span>
              <span class="cd-stat-value">
                {{ fmt(chart.stats.regression.slope) }}x + {{ fmt(chart.stats.regression.intercept) }}
              </span>
            </div>
            <div class="cd-stat-cell">
              <span class="cd-stat-label">R²</span>
              <span class="cd-stat-value">{{ fmt(chart.stats.regression.r2) }}</span>
            </div>
            <div class="cd-stat-cell">
              <span class="cd-stat-label">Pearson r</span>
              <span class="cd-stat-value">{{ fmt(chart.stats.regression.r) }}</span>
            </div>
            <div class="cd-stat-cell">
              <span class="cd-stat-label">n</span>
              <span class="cd-stat-value">{{ chart.stats.regression.n }}</span>
            </div>
          </div>
        </div>

        <!-- Series comparison -->
        <div v-if="chart.stats && chart.stats.series1 && chart.stats.series2" class="cd-stat-block">
          <h6><i class="fas fa-exchange-alt"></i> Series comparison</h6>
          <table class="cd-compare-table">
            <thead>
              <tr>
                <th></th>
                <th>Series 1</th>
                <th>Series 2</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in compareMetrics" :key="m">
                <td class="cd-compare-label">{{ m }}</td>
                <td>{{ fmt(chart.stats.series1[m]) }}</td>
                <td>{{ fmt(chart.stats.series2[m]) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Box plot groups -->
        <div v-if="chart.stats && chart.stats.groups && Array.isArray(chart.stats.groups)" class="cd-stat-block">
          <h6><i class="fas fa-layer-group"></i> Per-group statistics</h6>
          <div class="cd-table-wrap">
            <table class="cd-compare-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>n</th>
                  <th>min</th>
                  <th>Q1</th>
                  <th>median</th>
                  <th>Q3</th>
                  <th>max</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="g in chart.stats.groups" :key="g.label">
                  <td class="cd-compare-label">{{ g.label }}</td>
                  <td>{{ g.n }}</td>
                  <td>{{ fmt(g.min) }}</td>
                  <td>{{ fmt(g.q1) }}</td>
                  <td>{{ fmt(g.median) }}</td>
                  <td>{{ fmt(g.q3) }}</td>
                  <td>{{ fmt(g.max) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import Stats from '../services/StatsService.js'

export default {
  name: 'ChartDisplay',
  props: {
    chart: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null }
  },
  data () {
    return {
      chartInstance: null,
      compareMetrics: ['count', 'mean', 'median', 'stddev', 'min', 'max']
    }
  },
  computed: {
    chartJsAvailable () {
      return !!(this.$Chart || (typeof window !== 'undefined' && window.Chart))
    },
    hasStats () {
      if (!this.chart || !this.chart.stats) return false
      const s = this.chart.stats
      return !!(s.summary || s.regression || s.series1 || s.series2 ||
        (s.groups && Array.isArray(s.groups) && s.groups.length))
    }
  },
  watch: {
    chart: {
      handler () {
        this.$nextTick(() => this.renderChart())
      },
      deep: false
    }
  },
  mounted () {
    this.renderChart()
  },
  beforeDestroy () {
    this.destroyChart()
  },
  methods: {
    fmt (v) { return Stats.fmt(v, 3) },
    summaryRows (s) {
      return [
        { label: 'n', value: s.count },
        { label: 'Mean', value: Stats.fmt(s.mean) },
        { label: 'Median', value: Stats.fmt(s.median) },
        { label: 'Std dev', value: Stats.fmt(s.stddev) },
        { label: 'Min', value: Stats.fmt(s.min) },
        { label: 'Max', value: Stats.fmt(s.max) },
        { label: 'P10', value: Stats.fmt(s.p10) },
        { label: 'P25', value: Stats.fmt(s.p25) },
        { label: 'P75', value: Stats.fmt(s.p75) },
        { label: 'P90', value: Stats.fmt(s.p90) },
        { label: 'Range', value: Stats.fmt(s.range) },
        { label: 'Missing', value: s.missing }
      ]
    },
    renderChart () {
      if (!this.chart || !this.chartJsAvailable) return
      this.destroyChart()
      const canvas = this.$refs.canvas
      if (!canvas) return
      const ChartCtor = this.$Chart || window.Chart
      try {
        this.chartInstance = new ChartCtor(canvas.getContext('2d'), {
          type: this.chart.type,
          data: this.chart.data,
          options: this.chart.options
        })
        this.$emit('chartReady', this.chartInstance)
      } catch (err) {
        console.error('Chart render failed:', err)
        this.$emit('renderError', err)
      }
    },
    destroyChart () {
      if (this.chartInstance) {
        try { this.chartInstance.destroy() } catch (e) { /* ignore */ }
        this.chartInstance = null
      }
    }
  }
}
</script>

<style scoped>
.chart-display { width: 100%; }

.cd-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  background: #f8fbff;
  border: 1px dashed #cfe2ff;
  border-radius: 8px;
  min-height: 320px;
  text-align: center;
}
.cd-state-error {
  background: #fff9f9;
  border-color: #f5c2c7;
}

.cd-chart-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cd-canvas-wrap {
  position: relative;
  width: 100%;
  height: 460px;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 8px;
  padding: 12px;
}
.cd-canvas-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}

.cd-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}
.cd-stat-block {
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 8px;
  padding: 12px 14px;
}
.cd-stat-block h6 {
  font-size: 0.82rem;
  color: #0a4da8;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.cd-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
}
.cd-stat-cell {
  background: #fafbfc;
  border: 1px solid #eef1f4;
  border-radius: 4px;
  padding: 6px 9px;
  display: flex;
  flex-direction: column;
}
.cd-stat-label {
  font-size: 0.68rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.cd-stat-value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #212529;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}
.cd-compare-table {
  width: 100%;
  font-size: 0.82rem;
}
.cd-compare-table th {
  font-weight: 600;
  color: #495057;
  text-align: right;
  padding: 4px 6px;
  border-bottom: 1px solid #e5e9f0;
}
.cd-compare-table td {
  padding: 4px 6px;
  text-align: right;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}
.cd-compare-table .cd-compare-label {
  text-align: left;
  font-family: inherit;
  color: #495057;
  font-weight: 500;
}
.cd-table-wrap {
  overflow-x: auto;
  max-height: 220px;
  overflow-y: auto;
}
</style>
